import React, { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FiArrowLeft, FiArrowRight, FiBookOpen, FiHeart, FiPause, FiPlay } from "react-icons/fi";
import styles from "./Styles/DesktopLanding.module.css";

const hasText = (value) => typeof value === "string" && value.trim().length > 0;
const hasAction = (action) => hasText(action?.label) && hasText(action?.to);
const emptySlides = [];

function SlideLink({ action, children, ...props }) {
  return /^(#|https?:\/\/)/i.test(action.to)
    ? <a href={action.to} {...props}>{children}</a>
    : <Link to={action.to} {...props}>{children}</Link>;
}

export default function HeroCarousel({ slides: heroSlides = emptySlides, renderSlide, label = "معرفی راه بهشت", className = "" }) {
  const slidesId = useId();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const playing = !paused && !hovered && !focused && !reducedMotion;
  const advance = (step) => setActive((current) => (current + step + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    if (!playing || heroSlides.length < 2) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setActive((current) => (current + 1) % heroSlides.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [playing, active, heroSlides.length]);

  useEffect(() => { setActive(0); }, [heroSlides]);
  if (!heroSlides.length) return null;

  return (
    <section className={`${styles.hero} ${styles.heroCarousel} ${className}`} aria-label={label} aria-roledescription="اسلایدر"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault(); advance(event.key === "ArrowLeft" ? 1 : -1);
        }
      }}>
      <div className={styles.heroSlides} id={slidesId} aria-live={playing ? "off" : "polite"}>
        {heroSlides.map((slide, index) => {
          const hasHeading = [slide.title, slide.subtitle, slide.highlight].some(hasText);
          const hasActions = hasAction(slide.primary) || hasAction(slide.secondary);
          const hasCopy = hasHeading || hasActions || [slide.eyebrow, slide.description, slide.note].some(hasText);
          return (
          <div key={slide.id || index} className={`${styles.heroSlide} ${renderSlide || !hasCopy ? styles.heroSlideImageOnly : ""} ${index === active ? styles.heroSlideActive : ""}`}
            role="group" aria-roledescription="اسلاید" aria-label={`${index + 1} از ${heroSlides.length}`}
            aria-hidden={index !== active} inert={index !== active ? "" : undefined}>
            {renderSlide ? renderSlide(slide, index === active) : <>{hasCopy && <div className={styles.heroCopy}>
              {hasText(slide.eyebrow) && <span className={styles.eyebrow}><span />{slide.eyebrow}</span>}
              {hasHeading && <h1 id={index === active ? "landing-title" : undefined} style={Number.isInteger(slide.titleFontSize) && slide.titleFontSize >= 12 && slide.titleFontSize <= 120 ? { fontSize: slide.titleFontSize } : undefined}>
                {hasText(slide.title) && slide.title}
                {hasText(slide.title) && [slide.subtitle, slide.highlight].some(hasText) && <br />}
                {hasText(slide.subtitle) && slide.subtitle}
                {hasText(slide.subtitle) && hasText(slide.highlight) && " "}
                {hasText(slide.highlight) && <span>{slide.highlight}</span>}
              </h1>}
              {hasText(slide.description) && <p>{slide.description}</p>}
              {hasActions && <div className={styles.heroActions}>
                {hasAction(slide.primary) && <SlideLink action={slide.primary} className={styles.primaryButton} tabIndex={index === active ? undefined : -1}>{slide.primary.label}<FiArrowLeft aria-hidden="true" /></SlideLink>}
                {hasAction(slide.secondary) && <SlideLink action={slide.secondary} className={styles.textButton} tabIndex={index === active ? undefined : -1}>{slide.secondary.label}<span aria-hidden="true">↙</span></SlideLink>}
              </div>}
              {hasText(slide.note) && <div className={styles.heroNote}><span className={styles.smallStar} aria-hidden="true">✳</span><span>{slide.note}</span></div>}
            </div>}
            <div className={styles.heroVisual}>
              <div className={styles.visualArch} aria-hidden="true" /><div className={styles.orbit} aria-hidden="true" />
              <span className={styles.sparkle} aria-hidden="true">✳</span>
              <img className={styles.childImage} src={slide.image} alt={slide.imageAlt || ""} fetchpriority={index === 0 ? "high" : "auto"} />
              {[slide.bookTitle, slide.bookText].some(hasText) && <div className={styles.floatingBook}><span><FiBookOpen aria-hidden="true" /></span><div>{hasText(slide.bookTitle) && <strong>{slide.bookTitle}</strong>}{hasText(slide.bookText) && <small>{slide.bookText}</small>}</div></div>}
              {[slide.heartTitle, slide.heartText].some(hasText) && <div className={styles.floatingHeart}><FiHeart aria-hidden="true" /><span>{hasText(slide.heartTitle) && slide.heartTitle}{hasText(slide.heartTitle) && hasText(slide.heartText) && <br />}{hasText(slide.heartText) && <strong>{slide.heartText}</strong>}</span></div>}
              {hasText(slide.caption) && <span className={styles.visualCaption}>{slide.caption}</span>}
            </div></>}
          </div>
        ); })}
      </div>
      {heroSlides.length > 1 && <div className={styles.heroControls}>
        <button type="button" aria-label="اسلاید قبلی" aria-controls={slidesId} onClick={() => advance(-1)}><FiArrowRight aria-hidden="true" /></button>
        <div className={styles.heroDots}>{heroSlides.map((slide, index) => <button key={slide.id || index} type="button" aria-label={`نمایش اسلاید ${index + 1}${[slide.title, slide.subtitle, slide.highlight].some(hasText) ? `: ${[slide.title, slide.subtitle, slide.highlight].filter(hasText).join(" ")}` : ""}`} aria-current={active === index ? "true" : undefined} aria-controls={slidesId} onClick={() => setActive(index)}><span /></button>)}</div>
        <button type="button" aria-label="اسلاید بعدی" aria-controls={slidesId} onClick={() => advance(1)}><FiArrowLeft aria-hidden="true" /></button>
        {!reducedMotion && <button type="button" aria-label={paused ? "شروع پخش خودکار" : "توقف پخش خودکار"} aria-pressed={paused} onClick={() => setPaused((value) => !value)}>{paused ? <FiPlay aria-hidden="true" /> : <FiPause aria-hidden="true" />}</button>}
      </div>}
    </section>
  );
}
